// DashboardWidget — the school-admin "what's happening right now" view.
//
// Reads /schools/today/arrivals and renders a summary (total arrived,
// late count) above a chronological feed. Voided arrivals are filtered
// out of both — they're history, not "today".
//
// Visual: matches the parent-app TodayWidget aesthetic — page-header
// overline + heading, summary Card with stat chips, per-arrival Card
// rows with status pills.

import { useEffect, useState } from "react";

import {
  Alert,
  Card,
  EmptyState,
  Spinner,
  Stack,
  tokens,
} from "@sms/design-system";
import type { Locale } from "@sms/i18n";
import { Client, type ArrivalToday } from "@sms/sdk";

import { makeT } from "../../i18n";

export interface DashboardWidgetProps {
  locale?: Locale;
  client?: Pick<Client, "todayArrivals">;
}

type State =
  | { kind: "loading" }
  | { kind: "error" }
  | { kind: "ready"; arrivals: ArrivalToday[] };

const defaultClient = new Client({
  baseURL: typeof window !== "undefined" ? window.location.origin : "",
});

export function DashboardWidget({ locale = "ne", client = defaultClient }: DashboardWidgetProps) {
  const t = makeT(locale);
  const [state, setState] = useState<State>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    client
      .todayArrivals()
      .then((arrivals) => {
        if (!cancelled) setState({ kind: "ready", arrivals });
      })
      .catch(() => {
        if (!cancelled) setState({ kind: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [client]);

  return (
    <section aria-labelledby="dashboard-heading" style={containerStyle}>
      <header style={pageHeaderStyle}>
        <p style={overlineStyle}>{formatToday(locale)}</p>
        <h1 id="dashboard-heading" style={headingStyle}>
          {t("dashboard.title")}
        </h1>
      </header>

      {state.kind === "loading" && (
        <Stack gap={3} align="center" style={{ padding: tokens.space[10] }}>
          <Spinner size={28} label={t("common.loading")} />
        </Stack>
      )}

      {state.kind === "error" && (
        <Alert intent="danger">{t("dashboard.fetch_failed")}</Alert>
      )}

      {state.kind === "ready" && <Loaded arrivals={state.arrivals} t={t} locale={locale} />}
    </section>
  );
}

function Loaded({
  arrivals,
  t,
  locale,
}: {
  arrivals: ArrivalToday[];
  t: (k: string, v?: Record<string, string>) => string;
  locale: Locale;
}) {
  const live = arrivals.filter((a) => !a.voided);
  if (live.length === 0) {
    return (
      <EmptyState
        icon={
          <img
            src="/empty/no-wards.svg"
            alt=""
            width={120}
            height={120}
            style={{ display: "block" }}
          />
        }
        title={t("dashboard.no_arrivals_yet")}
        description={t("dashboard.no_arrivals_hint") || ""}
      />
    );
  }
  const lateCount = live.filter((a) => a.late).length;
  return (
    <Stack gap={4}>
      <Card variant="subtle" padding={4} data-testid="dashboard-summary">
        <Stack direction="row" gap={3} align="center" justify="space-between">
          <strong style={summaryStatStyle}>
            {t("dashboard.total_arrived", { count: String(live.length) })}
          </strong>
          {lateCount > 0 && (
            <span style={latePillStyle}>
              {t("dashboard.late_count", { count: String(lateCount) })}
            </span>
          )}
        </Stack>
      </Card>

      <Stack gap={2}>
        {live.map((a) => (
          <Card
            key={a.arrival_id}
            variant="raised"
            padding={4}
            data-testid={`arrival-${a.arrival_id}`}
            data-late={a.late ? "true" : "false"}
          >
            <Stack direction="row" gap={3} align="center">
              <Avatar initial={initialOf(a.student_name)} accent={accentForName(a.student_name)} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={nameStyle}>{a.student_name}</p>
                <p style={metaStyle}>{a.class_section}</p>
              </div>
              <span style={a.late ? latePillStyle : arrivedPillStyle}>
                {formatTime(a.arrived_at, locale)}
                {a.late ? ` · ${t("status.late")}` : ""}
              </span>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  );
}

function Avatar({ initial, accent }: { initial: string; accent: string }) {
  return (
    <div
      aria-hidden="true"
      style={{
        width: 40,
        height: 40,
        flexShrink: 0,
        borderRadius: tokens.radius.pill,
        background: accent,
        color: tokens.color.neutral[0],
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 16,
        fontWeight: tokens.type.weight.semibold,
      }}
    >
      {initial}
    </div>
  );
}

function formatTime(rfc3339: string, locale: Locale): string {
  const d = new Date(rfc3339);
  if (Number.isNaN(d.getTime())) return rfc3339;
  return new Intl.DateTimeFormat(locale === "ne" ? "ne-NP" : "en-NP", {
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "Asia/Kathmandu",
  }).format(d);
}

function formatToday(locale: Locale): string {
  return new Intl.DateTimeFormat(locale === "ne" ? "ne-NP" : "en-NP", {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "Asia/Kathmandu",
  }).format(new Date());
}

function initialOf(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const first = [...trimmed][0];
  return (first ?? "?").toUpperCase();
}

function accentForName(name: string): string {
  const accents = [
    "#dc2626", "#0284c7", "#16a34a", "#9333ea", "#ea580c", "#475569",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return accents[Math.abs(hash) % accents.length]!;
}

const containerStyle: React.CSSProperties = {
  width: "100%",
  maxWidth: 640,
  margin: "0 auto",
  padding: tokens.space[4],
};
const pageHeaderStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: tokens.space[1],
  marginBottom: tokens.space[5],
};
const overlineStyle: React.CSSProperties = {
  fontSize: tokens.type.size.xs,
  fontWeight: tokens.type.weight.semibold,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: tokens.color.semantic.textMuted,
};
const headingStyle: React.CSSProperties = {
  fontSize: 28,
  fontWeight: tokens.type.weight.bold,
  color: tokens.color.semantic.heading,
  letterSpacing: "-0.02em",
  lineHeight: 1.2,
  margin: 0,
};
const summaryStatStyle: React.CSSProperties = {
  fontSize: tokens.type.size.lg,
  fontWeight: tokens.type.weight.semibold,
  color: tokens.color.semantic.heading,
  letterSpacing: "-0.01em",
};
const nameStyle: React.CSSProperties = {
  fontSize: tokens.type.size.base,
  fontWeight: tokens.type.weight.semibold,
  color: tokens.color.semantic.heading,
  margin: 0,
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};
const metaStyle: React.CSSProperties = {
  fontSize: tokens.type.size.sm,
  color: tokens.color.semantic.textMuted,
  margin: "2px 0 0",
};
const pillBase: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  borderRadius: tokens.radius.pill,
  fontSize: tokens.type.size.xs,
  fontWeight: tokens.type.weight.semibold,
  whiteSpace: "nowrap",
};
const arrivedPillStyle: React.CSSProperties = {
  ...pillBase,
  background: tokens.color.success[50],
  color: tokens.color.success[700],
};
const latePillStyle: React.CSSProperties = {
  ...pillBase,
  background: tokens.color.warning[50],
  color: tokens.color.warning[700],
};
